import { Component, ElementRef, inject, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ActionSheetController, AlertController, IonContent, ModalController, NavController, ToastController } from '@ionic/angular';
import { TaskService } from 'src/services/task.service';
import { Task } from '../../../models/task.model';
import { ActivatedRoute } from '@angular/router';
import { Priority } from '../../../models/priority.model';
import { environment } from '../../../environments/environment';
import {
    FullscreenFileImageModalComponent
} from '../fullscreen-file-image-modal/fullscreen-file-image-modal.component';
import { TaskFormComponent } from '../task-form/task-form.component';
import { TaskCommentDto } from '../../../models/task-comment.dto';
import { UserService } from '../../../services/user.service';
import { User } from '../../../models/user.model';
import { UrlUtils } from '../../../components/url.utils';
import { TaskCommentReadStateService } from '../../../services/task-comment-read-state.service';

@Component({
    selector: 'app-task-details',
    templateUrl: './task-details.page.html',
    styleUrls: ['./task-details.page.scss'],
    standalone: false
})
export class TasksDetailsPage implements OnChanges {

    /** Task ID passed from parent in embedded mode */
    @Input() taskId: number | null = null;

    /** Whether this component is embedded in a master-detail layout */
    @Input() embedded: boolean = false;

    @ViewChild(IonContent, {static: true}) ionContent: IonContent;
    @ViewChild('taskComments', {read: ElementRef, static: false}) taskCommentsRef: ElementRef;

    protected isLoading = false;
    protected isSaving = false;

    protected task: Task | null = null;
    protected taskCommentDto: TaskCommentDto = new TaskCommentDto();
    /** Guards the comment submit against double-clicks (drives the button disabled + spinner). */
    protected isSavingComment = false;

    protected activeUser: User | null = null;
    protected currentUserId: number | null = null;

    protected readonly Priority = Priority; // To expose the enum to the template

    private readonly taskService: TaskService = inject(TaskService);
    private readonly commentReadState: TaskCommentReadStateService = inject(TaskCommentReadStateService);
    private readonly userService: UserService = inject(UserService);
    private readonly route: ActivatedRoute = inject(ActivatedRoute);

    private readonly navController: NavController = inject(NavController);

    private readonly modalController: ModalController = inject(ModalController);
    private readonly toastController: ToastController = inject(ToastController);
    private readonly alertController: AlertController = inject(AlertController);
    private readonly actionSheetController: ActionSheetController = inject(ActionSheetController);

    /**
     * React to input changes in embedded mode.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['taskId'] && this.taskId) {
            this.loadAllData();
        }
    }

    protected getAvatarUrl(user: { profilePictureUrl?: string | null } | null | undefined): string | null {
        return UrlUtils.getFullImageUrl(user?.profilePictureUrl);
    }

    /**
     * Load data when navigating to this page (non-embedded mode).
     */
    public async ionViewWillEnter(): Promise<void> {
        // In embedded mode, ngOnChanges handles loading
        if (this.embedded) {
            return;
        }
        await this.loadAllData();
    }

    /**
     * Load all data for the task details view.
     */
    private async loadAllData(): Promise<void> {
        await this.loadTask();
        await this.loadCurrentUser();
    }

    protected getImageUrl(image: string): any {
        return environment.fileUrl + '/' + image;
    }

    protected async showImageFullscreen(imageUrl: string): Promise<void> {
        const trueUrl = environment.fileUrl + '/' + imageUrl;
        const imageModal = await this.modalController.create({
            component: FullscreenFileImageModalComponent,
            componentProps: {
                imageUrl: trueUrl
            }
        });
        await imageModal.present();
    }

    protected getPriorityColor(priority: Priority): any {
        switch (priority) {
            case Priority.VeryHigh:
                return 'danger';
            case Priority.High:
                return 'warning';
            case Priority.Low:
                return 'medium';
            case Priority.VeryLow:
                return 'light';
            default:
                return 'light';

        }
    }

    protected getPriorityString(priority: Priority): string {
        switch (priority) {
            case Priority.VeryHigh:
                return 'Zeer hoge prioriteit';
            case Priority.High:
                return 'Hoge prioriteit';
            case Priority.Medium:
                return 'Normale prioriteit';
            case Priority.Low:
                return 'Lage prioriteit';
            case Priority.VeryLow:
                return 'Zeer lage prioriteit';
            default:
                return 'Prioriteit niet bekend';
        }
    }

    // TODO RS: Refactor this method
    protected toggleActionSheet(): void {

        let buttons = [];

        if (this.task.completedBy == null) {
            buttons = [
                {
                    text: 'Klus afvinken',
                    icon: 'checkmark-outline',
                    handler: async () => {
                        await this.completeTask();
                    }
                },
                {
                    text: 'Klus wijzigen',
                    icon: 'create-outline',
                    handler: async () => {
                        await this.navigateToEditTaskPage();
                    }
                },
                {
                    text: 'Annuleren',
                    icon: 'close',
                    role: 'cancel'
                }
            ];
        } else {
            buttons = [
                {
                    text: 'Klus heropenen',
                    icon: 'arrow-undo-circle-outline',
                    handler: async () => {
                        await this.confirmReopenTask();
                    }
                },
                {
                    text: 'Annuleren',
                    icon: 'close',
                    role: 'cancel'
                }
            ];

        }

        this.actionSheetController
            .create({
                buttons: buttons
            })
            .then(actionSheetEl => {
                actionSheetEl.present();
            });
    }

    protected scrollToLastComment(): void {
        setTimeout(() => {
            this.ionContent.scrollToBottom(300);
        }, 300);
    }

    protected async saveNewComment(): Promise<void> {
        if (this.isSavingComment) {
            return;
        }
        this.taskCommentDto.comment = this.taskCommentDto.comment.trim();
        if (this.taskCommentDto.comment.length === 0) {
            return;
        }
        this.isSavingComment = true;
        try {
            const response = await this.taskService.addComment(this.task.id, this.taskCommentDto);
            this.task = response.data[0];
            this.taskCommentDto.comment = '';
            this.scrollToLastComment();
            await this.loadTask();
        } finally {
            this.isSavingComment = false;
        }
    }

    protected async navigateToEditTaskPage(): Promise<void> {
        await this.openNewTaskModal();
    }

    /**
     * Get the effective task ID from either the Input or the route.
     */
    private getEffectiveTaskId(): number | null {
        // In embedded mode, use the Input
        if (this.taskId) {
            return this.taskId;
        }
        // Otherwise, get from route
        const id = this.route.snapshot.paramMap.get('id');
        return id ? parseInt(id, 10) : null;
    }

    private async reopenTask(): Promise<void> {
        this.task.completed = false;
        this.task.completedDateTime = null;
        this.task.completedBy = null;
        this.isSaving = true;
        try {
            const response = await this.taskService.changeTaskCompletionStatus(this.task.id, false);

            await this.toastController
                .create({
                    message: 'Taak is heropend!',
                    duration: 500,
                    color: 'success'
                })
                .then(toast => toast.present());

            if (response[0]?.completedBy == null) {
                await this.navController.navigateRoot('/tasks');
            }
        } catch (e) {
            console.error('[TaskComponent] handleCompleteTask() - error: ', e);
            this.toastController.create({
                message: 'Er ging iets mis, probeer het nogmaals aub',
                color: 'danger',
                duration: 2000
            }).then(toast => toast.present());
        } finally {
            this.isSaving = false;
        }
    }

    private async confirmReopenTask(): Promise<void> {

        const alert = this.alertController.create({
                header: 'Klus heropenen?',
                message: 'Weet je zeker dat je deze klus wilt heropenen?',
                buttons: [
                    {
                        text: 'Annuleren',
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: async () => {
                        }
                    }, {
                        text: 'Heropenen',
                        handler: async () => {
                            await this.reopenTask();

                        }
                    }
                ]
            }
        );

        await (await alert).present();

    }

    private async openNewTaskModal(): Promise<void> {
        const modal = await this.modalController.create({
            component: TaskFormComponent,
            componentProps: {
                task: this.task
            }
        });
        await modal.present();
        await modal.onWillDismiss();
        await this.loadTask();
    }

    private async loadTask(): Promise<void> {
        const taskId = this.getEffectiveTaskId();
        if (!taskId) {
            return;
        }

        this.isLoading = true;

        try {
            this.task = await this.taskService.getTaskById(taskId);
            // Opening the task means the user has now seen its comments —
            // clears the "new comments" marker for this task (device-local).
            this.commentReadState.markSeen(this.task);
        } catch (error) {
            console.error('[TasksDetailsPage]: loadTask: error: ', error);
        } finally {
            this.isLoading = false;
        }
    }

    private async loadCurrentUser(): Promise<void> {
        this.currentUserId = await this.userService.getActiveUserId();
        this.activeUser = await this.userService.getActiveUser();
    }

    private async completeTask(): Promise<void> {

        this.task.completed = true;
        this.task.completedDateTime = new Date().toDateString();

        this.isSaving = true;

        try {
            const response = await this.taskService.changeTaskCompletionStatus(this.task.id, true);

            await this.toastController
                .create({
                    message: 'Taak is afgevinkt!',
                    duration: 500,
                    color: 'success'
                })
                .then(toast => toast.present());

            if (response[0]?.completedBy != null) {
                await this.navController.navigateRoot('/tasks');
            }

        } catch (e) {
            console.error('[TaskComponent] handleCompleteTask() - error: ', e);
            this.toastController.create({
                message: 'Er ging iets mis, probeer het nogmaals aub',
                color: 'danger',
                duration: 2000
            }).then(toast => toast.present());
        } finally {
            this.isSaving = false;
        }
    }

}
